import * as React from "react";
import { Loader2, Database } from "lucide-react";
import {
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
} from "../../../components/ui-v2/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui-v2/table";
import { Text, MonoText } from "../../../components/ui-v2/typography";
import { DYNAMO_COLORS } from "./constants";

function DynamoDbPieChart({ dynamoDb }) {
  if (!dynamoDb || dynamoDb.total === 0) {
    return (
      <div className="h-[200px] flex items-center justify-center">
        <Text variant="secondary" size="sm">No DynamoDB operations recorded</Text>
      </div>
    );
  }

  const data = Object.entries(dynamoDb.operations)
    .filter(([, count]) => count > 0)
    .map(([name, count], index) => ({
      name,
      value: count,
      color: DYNAMO_COLORS[index % DYNAMO_COLORS.length],
    }));

  return (
    <ResponsiveContainer width="100%" height={200}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={70}
          innerRadius={40}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip />
        <Legend
          iconSize={8}
          wrapperStyle={{ fontSize: "11px" }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

function DynamoDbTable({ dynamoDb }) {
  if (!dynamoDb) return null;

  const rows = Object.entries(dynamoDb.breakdown || {})
    .sort(([, a], [, b]) => b.count - a.count);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Operation</TableHead>
          <TableHead className="text-right">Count</TableHead>
          <TableHead className="text-right">Percentage</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map(([operation, stats]) => (
          <TableRow key={operation}>
            <TableCell>
              <MonoText size="sm">{operation}</MonoText>
            </TableCell>
            <TableCell className="text-right">
              <MonoText size="sm">{stats.count}</MonoText>
            </TableCell>
            <TableCell className="text-right">
              <MonoText size="sm">{stats.percentage}</MonoText>
            </TableCell>
          </TableRow>
        ))}
        <TableRow className="font-semibold border-t-2">
          <TableCell>Total</TableCell>
          <TableCell className="text-right">
            <MonoText size="sm" weight="bold">{dynamoDb.total}</MonoText>
          </TableCell>
          <TableCell className="text-right">
            <MonoText size="sm" weight="bold">100%</MonoText>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

export function DynamoDbSection({ loading, dynamoDb }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Database className="h-4 w-4 text-primary" />
            DynamoDB Operations
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          {loading ? (
            <div className="h-[200px] flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <DynamoDbPieChart dynamoDb={dynamoDb} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Database className="h-4 w-4 text-primary" />
            Operations Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          {loading ? (
            <div className="h-[200px] flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <DynamoDbTable dynamoDb={dynamoDb} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default DynamoDbSection;
